import { useEffect, useState } from "react";
import { GitMerge } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fileUrl } from "../../lib/api";

export default function DuplicatesTab() {
    const [duplicates, setDuplicates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [merging, setMerging] = useState("");

    const load = () => {
        setLoading(true);
        api.get("/admin/duplicate-athletes")
            .then((res) => setDuplicates(res.data))
            .catch(() => toast.error("Gagal memuat data ganda"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, []);

    const merge = async (athleteId, studentId) => {
        const key = `${athleteId}:${studentId}`;
        setMerging(key);
        try {
            const { data } = await api.post("/admin/merge-athlete", { athlete_id: athleteId, student_id: studentId });
            toast.success(data.message);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Gagal menggabungkan data");
        } finally {
            setMerging("");
        }
    };

    return (
        <div data-testid="duplicates-tab">
            <h2 className="font-display uppercase text-2xl text-neutral-900 mb-2">Gabung Data Ganda</h2>
            <p className="text-sm text-racing-muted mb-8 max-w-3xl">
                Sistem mendeteksi atlet di daftar publik yang namanya mirip dengan data siswa di portal orang tua. Klik "Gabungkan" pada pasangan yang benar — setelah digabung, NIA, foto, dan prestasi atlet publik akan terhubung dengan akun siswa sehingga data menjadi satu.
            </p>

            {loading && <p className="text-racing-muted" data-testid="duplicates-loading">Memindai data...</p>}

            {!loading && duplicates.length === 0 && (
                <p className="border border-racing-line bg-white p-6 text-sm text-racing-muted" data-testid="duplicates-empty">
                    Tidak ada data ganda yang terdeteksi. Semua atlet publik sudah terhubung dengan data siswa.
                </p>
            )}

            <div className="space-y-5">
                {duplicates.map((dup, i) => (
                    <div key={dup.athlete.id} className="border border-racing-line bg-white p-5 md:p-6" data-testid={`duplicate-card-${i}`}>
                        <div className="flex items-center gap-4 mb-4 pb-4 border-b border-racing-line">
                            {dup.athlete.photo_url ? (
                                <img src={fileUrl(dup.athlete.photo_url)} alt="" className="w-14 h-14 object-cover border border-racing-line" />
                            ) : (
                                <span className="w-14 h-14 bg-racing-surface border border-racing-line flex items-center justify-center font-display text-xl text-racing-red">{dup.athlete.name.charAt(0)}</span>
                            )}
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted">Atlet Publik (website)</p>
                                <p className="font-display uppercase text-xl text-neutral-900" data-testid={`duplicate-athlete-name-${i}`}>
                                    {dup.athlete.name} {dup.athlete.nia && <span className="text-racing-golddeep text-sm">• NIA {dup.athlete.nia}</span>}
                                </p>
                                <p className="text-xs text-racing-muted">{dup.athlete.class_name || "Tanpa kelas"}{dup.athlete.joined_year ? ` • sejak ${dup.athlete.joined_year}` : ""}</p>
                            </div>
                        </div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-3">Kandidat data siswa yang mirip:</p>
                        <div className="space-y-2">
                            {dup.candidates.map((c, ci) => (
                                <div key={c.id} className="flex flex-wrap items-center gap-x-5 gap-y-2 border border-racing-line bg-racing-surface px-4 py-3" data-testid={`duplicate-candidate-${i}-${ci}`}>
                                    <div className="min-w-0 flex-1">
                                        <p className="font-medium text-sm text-neutral-900">{c.name}</p>
                                        <p className="text-xs text-racing-muted">{c.class_name || "Tanpa kelas"} • {c.status}{c.birth_date ? ` • lahir ${c.birth_date}` : ""}{c.parent_phone ? ` • ${c.parent_phone}` : ""}</p>
                                    </div>
                                    <button
                                        onClick={() => merge(dup.athlete.id, c.id)}
                                        disabled={merging === `${dup.athlete.id}:${c.id}`}
                                        data-testid={`duplicate-merge-${i}-${ci}`}
                                        className="flex items-center gap-2 bg-racing-red hover:bg-racing-redhover disabled:opacity-50 text-white text-[10px] uppercase tracking-[0.15em] px-4 py-2.5 transition-colors"
                                    >
                                        <GitMerge size={15} weight="bold" /> {merging === `${dup.athlete.id}:${c.id}` ? "Menggabungkan..." : "Gabungkan"}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
